// Browser downloads for the asset pipeline: texture sources as PNG and JSON
// metadata, via a temporary <a download> element.

import type Phaser from 'phaser';

function clickDownload(href: string, filename: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** PNG data URL of a texture's first source image (canvas or image), or null. */
export function textureDataUrl(scene: Phaser.Scene, key: string): string | null {
  if (!scene.textures.exists(key)) return null;
  const src = scene.textures.get(key).getSourceImage() as HTMLCanvasElement | HTMLImageElement | ImageBitmap | null;
  if (!src) return null;
  if (src instanceof HTMLCanvasElement) return src.toDataURL('image/png');
  const canvas = document.createElement('canvas');
  canvas.width = src.width;
  canvas.height = src.height;
  const g = canvas.getContext('2d');
  if (!g) return null;
  g.drawImage(src as CanvasImageSource, 0, 0);
  return canvas.toDataURL('image/png');
}

/** Download a texture's source as PNG. Returns a status line for the UI. */
export function downloadTexture(scene: Phaser.Scene, key: string | undefined, filename: string): string {
  if (!key) return `${filename}: no texture key`;
  try {
    const url = textureDataUrl(scene, key);
    if (!url) return `${filename}: texture "${key}" missing`;
    clickDownload(url, filename);
    return `exported ${filename}`;
  } catch (err) {
    return `${filename}: ${String(err)}`;
  }
}

/** Download any JSON-serialisable value. */
export function downloadJson(value: unknown, filename: string): string {
  if (value === undefined || value === null) return `${filename}: no metadata registered`;
  try {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    clickDownload(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return `exported ${filename}`;
  } catch (err) {
    return `${filename}: ${String(err)}`;
  }
}
